import {
  AlertCircle,
  CheckCircle2,
  FileAudio,
  FileText,
  Layers,
  Loader2,
  Trash2,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import type { Case } from '@/services/api/cases'
import { casesApi } from '@/services/api/cases'
import { apiClient } from '@/services/api/client'
import { type ExtractionScope, extractionScopeApi } from '@/services/api/extraction-scope'
import type { LakeDocument, TypesenseChunkRecord } from '@/services/api/lake'
import { lakeApi } from '@/services/api/lake'
import { isAudioFileType } from '@/utils/audio-playback'
import { DocxPageSelector } from './DocxPageSelector'
import { ImagePageSelector } from './ImagePageSelector'
import { PageChunkList } from './PageChunkList'
import { PdfPageSelector, type PdfViewMode } from './PdfPageSelector'

// ---------------------------------------------------------------------------
// Image file types that can be displayed inline
// ---------------------------------------------------------------------------

const IMAGE_FILE_TYPES = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif'])

// ---------------------------------------------------------------------------
// Structured document types — use page-by-page vision extraction, not chunks
// ---------------------------------------------------------------------------

const STRUCTURED_DOC_TYPES = new Set([
  'bank_statement',
  'invoice',
  'identification_document',
  'id_card',
  'drivers_license',
  'passport',
  'diploma',
  'insurance_card',
  'legal_document',
])

// Viewer + sidebar layouts: side by side from lg up; below it the viewer keeps a fixed
// share of the screen, the sidebar follows and the whole body scrolls.
const SPLIT_BODY = 'flex flex-1 min-h-0 overflow-hidden max-lg:flex-col max-lg:overflow-y-auto'
const SPLIT_VIEWER =
  'flex-1 min-w-0 border-r border-border bg-background flex flex-col min-h-0 max-lg:h-[62dvh] max-lg:flex-none max-lg:border-r-0 max-lg:border-b'
const SPLIT_SIDEBAR =
  'w-96 flex-shrink-0 flex flex-col overflow-hidden bg-card max-lg:w-full max-lg:overflow-visible'

interface ExtractionScopeModalProps {
  document: LakeDocument
  isOpen: boolean
  onClose: () => void
  onScopeCreated?: () => void
  /** If set, pre-select this chunk and navigate to its page */
  initialChunkId?: string
  initialChunkPageNumber?: number
  /** If true, skip chunk selection and create a whole-document scope */
  selectWholeDocument?: boolean
  /** If true, show the modal in read-only mode (no editing, no submit/delete) */
  readOnly?: boolean
  /** If set in readOnly mode, auto-selects this case and loads its scope */
  preSelectedCaseId?: string
}

export function ExtractionScopeModal({
  document,
  isOpen,
  onClose,
  onScopeCreated,
  initialChunkId,
  initialChunkPageNumber,
  selectWholeDocument = false,
  readOnly = false,
  preSelectedCaseId,
}: ExtractionScopeModalProps) {
  const pdfPageCount: number | null = document.analysis_result?.pdf_analysis?.page_count ?? null
  // PDFs always go through the preview path — PdfPageSelector self-discovers
  // its page count via react-pdf's onLoadSuccess, so a missing or zero
  // pdf_analysis.page_count from the lake analyzer is not a blocker.
  const isPdf = document.file_type === 'pdf'
  const isDocx = document.file_type === 'docx' && (document.has_pdf_preview ?? false)
  const isImage = IMAGE_FILE_TYPES.has(document.file_type || '')
  // For audio the modal only offers "import whole file" (no page selector).
  const isAudio = isAudioFileType(document.file_type)
  // Images are treated as single-page documents. For PDFs, pageCount may be
  // null until react-pdf loads — downstream consumers either guard on null
  // or pass it through to PdfPageSelector which falls back to its loaded count.
  const pageCount = isImage ? 1 : pdfPageCount
  // DOCX with converted PDF uses the same page-selector layout as narrative PDFs.
  // pageCount for DOCX comes from react-pdf reading the converted PDF via onLoadSuccess.
  // Images use a simplified single-page layout.
  const usePdfLayout = isPdf || isDocx
  const useImageLayout = isImage
  const documentType: string | null = document.analysis_result?.document_type ?? null
  // Structured docs include PDFs with structured types AND images (which are often ID documents)
  const isStructuredDoc =
    (isPdf && documentType !== null && STRUCTURED_DOC_TYPES.has(documentType)) || isImage

  // ── Cases & existing scopes ──────────────────────────────────────────────
  const [cases, setCases] = useState<Case[]>([])
  const [existingScopes, setExistingScopes] = useState<ExtractionScope[]>([])
  const [loading, setLoading] = useState(false)

  // ── Form state ──────────────────────────────────────────────────────────
  const [selectedCaseId, setSelectedCaseId] = useState('')
  const [scopeNote, setScopeNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // ── PDF / chunk selection state ──────────────────────────────────────────
  // `selectedPages` tracks pages where ALL chunks are checked (drives the page toggle UI)
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set())
  // `selectedChunkIds` is the authoritative source for submission (chunk-based docs)
  const [selectedChunkIds, setSelectedChunkIds] = useState<Set<string>>(new Set())
  // `selectedPageNumbers` is used for structured-doc (vision) page selection
  const [selectedPageNumbers, setSelectedPageNumbers] = useState<Set<number>>(new Set())
  // Cache of page → chunks (fetched lazily)
  const [chunksByPage, setChunksByPage] = useState<Map<number, TypesenseChunkRecord[]>>(new Map())
  const [loadingChunksForPage, setLoadingChunksForPage] = useState<number | null>(null)

  const [viewMode, setViewMode] = useState<PdfViewMode>('single')
  const [currentPage, setCurrentPage] = useState<number>(initialChunkPageNumber ?? 1)

  // ── Stable PDF source reference ─────────────────────────────────────────
  const pdfSource = useMemo(() => {
    const url = lakeApi.getFileUrl(document.s3_key)
    const token = apiClient.getToken()
    return { url, httpHeaders: token ? { Authorization: `Bearer ${token}` } : {} }
  }, [document.s3_key])

  // ── Load cases + scopes on open ─────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    setError(null)
    setSuccessMsg(null)
    Promise.all([casesApi.list(), extractionScopeApi.list(document.id)])
      .then(([c, s]) => {
        setCases(c)
        setExistingScopes(s)
      })
      .catch(() => setError('Failed to load cases'))
      .finally(() => setLoading(false))
  }, [isOpen, document.id])

  // ── Reset selection state when the modal opens ──────────────────────────
  useEffect(() => {
    if (!isOpen) return
    setSelectedChunkIds(new Set())
    setSelectedPageNumbers(new Set())
    // Pre-select all pages when opening in whole-document mode for structured PDFs
    // Also pre-select page 1 for images (single-page documents)
    if (selectWholeDocument && pageCount) {
      setSelectedPageNumbers(new Set(Array.from({ length: pageCount }, (_, i) => i + 1)))
      // FIX: also pre-populate selectedPages so handleTogglePage can detect deselect
      setSelectedPages(new Set(Array.from({ length: pageCount }, (_, i) => i + 1)))
    } else if (isImage) {
      // Images are single-page; pre-select page 1 for convenience
      setSelectedPageNumbers(new Set([1]))
      setSelectedPages(new Set([1]))
    } else {
      setSelectedPages(new Set())
    }
    setChunksByPage(new Map())
    setCurrentPage(initialChunkPageNumber ?? 1)
    setSelectedCaseId(preSelectedCaseId ? preSelectedCaseId : '')
    setScopeNote('')
    setError(null)
    setSuccessMsg(null)
  }, [isOpen, selectWholeDocument, pageCount, initialChunkPageNumber, preSelectedCaseId, isImage])

  // ── Fetch chunks for the current page (lazy, cached) ───────────────────
  const fetchChunksForPage = useCallback(
    async (page: number) => {
      if (!usePdfLayout) return
      if (chunksByPage.has(page)) return // already cached
      setLoadingChunksForPage(page)
      try {
        const res = await lakeApi.getChunksByPage(document.id, page)
        setChunksByPage(prev => new Map(prev).set(page, res.chunks))
      } catch {
        setChunksByPage(prev => new Map(prev).set(page, []))
      } finally {
        setLoadingChunksForPage(null)
      }
    },
    [document.id, chunksByPage, usePdfLayout]
  )

  // Fetch chunks when single-view page changes
  useEffect(() => {
    if (viewMode === 'single' && usePdfLayout) {
      fetchChunksForPage(currentPage)
    }
  }, [currentPage, viewMode, usePdfLayout, fetchChunksForPage])

  // ── Pre-select initial chunk once its page is loaded ───────────────────
  useEffect(() => {
    if (!initialChunkId || !usePdfLayout) return
    const pageChunks = chunksByPage.get(initialChunkPageNumber ?? 1)
    if (!pageChunks) return
    // Only pre-select once (if not already selected)
    setSelectedChunkIds(prev => {
      if (prev.has(initialChunkId)) return prev
      return new Set(prev).add(initialChunkId)
    })
  }, [initialChunkId, initialChunkPageNumber, chunksByPage, usePdfLayout])

  // ── Pre-fill selection from any existing import for the chosen case ──
  //
  // Fires in both read-only mode and edit mode: as soon as the user picks
  // (or the modal is opened with) a case that already has an import, we
  // restore that import's scope so re-imports show "what you previously
  // chose" and a small "Updating existing import" hint can render.
  const activeCaseId = readOnly ? (preSelectedCaseId ?? '') : selectedCaseId
  useEffect(() => {
    if (!activeCaseId || existingScopes.length === 0) return
    const scope = existingScopes.find(s => s.case_id === activeCaseId)
    if (!scope) return
    if (scope.scope_type === 'chunks' && scope.selected_chunk_ids?.length) {
      setSelectedChunkIds(new Set(scope.selected_chunk_ids))
    }
    if (scope.scope_type === 'pages' && scope.selected_pages?.length) {
      setSelectedPageNumbers(new Set(scope.selected_pages))
    }
    // When scope_type is "all", select all pages to visually indicate whole document
    if (scope.scope_type === 'all' && pageCount) {
      const allPages = new Set(Array.from({ length: pageCount }, (_, i) => i + 1))
      setSelectedPageNumbers(allPages)
      setSelectedPages(allPages)
    }
    if (scope.scope_note) {
      setScopeNote(scope.scope_note)
    }
  }, [activeCaseId, existingScopes, pageCount])

  // ── Page toggle (select/deselect all chunks on a page) ──────────────────
  const handleTogglePage = useCallback(
    async (page: number) => {
      // Ensure chunks are loaded first
      let pageChunks = chunksByPage.get(page)
      if (!pageChunks) {
        setLoadingChunksForPage(page)
        try {
          const res = await lakeApi.getChunksByPage(document.id, page)
          pageChunks = res.chunks
          setChunksByPage(prev => new Map(prev).set(page, res.chunks))
        } catch {
          pageChunks = []
          setChunksByPage(prev => new Map(prev).set(page, []))
        } finally {
          setLoadingChunksForPage(null)
        }
      }

      const chunkIds = pageChunks.map(c => c.id)

      setSelectedPages(prev => {
        const nextPages = new Set(prev)
        if (nextPages.has(page)) {
          // Deselect: remove page + all its chunks
          nextPages.delete(page)
          setSelectedChunkIds(prevChunks => {
            const next = new Set(prevChunks)
            chunkIds.forEach(id => next.delete(id))
            return next
          })
        } else {
          // Select: add page + all its chunks
          nextPages.add(page)
          setSelectedChunkIds(prevChunks => {
            const next = new Set(prevChunks)
            chunkIds.forEach(id => next.add(id))
            return next
          })
        }
        return nextPages
      })
    },
    [chunksByPage, document.id]
  )

  // ── Individual chunk toggle ──────────────────────────────────────────────
  const handleToggleChunk = useCallback(
    (chunkId: string) => {
      setSelectedChunkIds(prev => {
        const next = new Set(prev)
        if (next.has(chunkId)) {
          next.delete(chunkId)
        } else {
          next.add(chunkId)
        }

        // Update selectedPages based on whether all chunks on each loaded page are selected
        setSelectedPages(prevPages => {
          const nextPages = new Set(prevPages)
          // Check all loaded pages to keep selectedPages accurate
          chunksByPage.forEach((chunks, page) => {
            const allSelected = chunks.length > 0 && chunks.every(c => next.has(c.id))
            if (allSelected) {
              nextPages.add(page)
            } else {
              nextPages.delete(page)
            }
          })
          return nextPages
        })

        return next
      })
    },
    [chunksByPage]
  )

  // ── Submit ───────────────────────────────────────────────────────────────
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false)

  const handleSubmit = () => {
    if (!selectedCaseId) {
      setError('Please select a case')
      return
    }

    const existing = existingScopes.find(s => s.case_id === selectedCaseId)
    if (existing) {
      setReplaceConfirmOpen(true)
      return
    }
    void executeSubmit()
  }

  const executeSubmit = async () => {
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)

    try {
      let newScope: ExtractionScope
      if (isStructuredDoc) {
        // Page-based scope for vision-extracted structured documents (bank statements, etc.)
        const hasPageSelection = selectedPageNumbers.size > 0
        newScope = await extractionScopeApi.create(document.id, {
          case_id: selectedCaseId,
          scope_type: hasPageSelection ? 'pages' : 'all',
          selected_pages: hasPageSelection
            ? Array.from(selectedPageNumbers).sort((a, b) => a - b)
            : null,
          scope_note: scopeNote || null,
        })
      } else {
        // Narrative PDF / DOCX: the new backend supports only 'all' or 'pages'.
        // Prefer explicit `selectedPages` (set when the user toggles page
        // cards) since it works even when Typesense chunks haven't loaded.
        // Fall back to deriving pages from `selectedChunkIds` for users who
        // pick individual chunks on a page.
        let pageList: number[] = []
        if (usePdfLayout && selectedPages.size > 0) {
          pageList = [...selectedPages].sort((a, b) => a - b)
        } else if (usePdfLayout && selectedChunkIds.size > 0) {
          const pages = new Set<number>()
          chunksByPage.forEach((chunks, page) => {
            for (const c of chunks) {
              if (selectedChunkIds.has(c.id)) pages.add(page)
            }
          })
          pageList = [...pages].sort((a, b) => a - b)
        }
        const hasPages = pageList.length > 0
        newScope = await extractionScopeApi.create(document.id, {
          case_id: selectedCaseId,
          scope_type: hasPages ? 'pages' : 'all',
          selected_pages: hasPages ? pageList : null,
          scope_note: scopeNote || null,
        })
      }

      setExistingScopes(prev => {
        const filtered = prev.filter(s => s.id !== newScope.id)
        return [...filtered, newScope]
      })

      const caseName = cases.find(c => c.id === selectedCaseId)?.name || 'case'
      setSuccessMsg(`Imported to "${caseName}"`)
      setSelectedCaseId('')
      setScopeNote('')
      setSelectedPages(new Set())
      setSelectedChunkIds(new Set())
      setSelectedPageNumbers(new Set())

      onScopeCreated?.()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create case import')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteScope = async (scopeId: string) => {
    try {
      await extractionScopeApi.delete(document.id, scopeId)
      setExistingScopes(prev => prev.filter(s => s.id !== scopeId))
    } catch {
      setError('Failed to delete import')
    }
  }

  // ── Rendered current-page chunks ────────────────────────────────────────
  const currentPageChunks = chunksByPage.get(currentPage) ?? null

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        className={
          !usePdfLayout && !useImageLayout
            ? 'max-w-lg w-full p-0 flex flex-col overflow-hidden max-h-[80vh]'
            : 'w-[95vw] max-w-none sm:max-w-none h-[92dvh] p-0 flex flex-col overflow-hidden md:h-[90vh]'
        }
      >
        <DialogHeader className="px-4 py-3 border-b border-border flex-shrink-0 flex flex-row items-center justify-between">
          <DialogTitle className="text-sm font-semibold text-foreground flex items-center gap-2 max-md:min-w-0 max-md:break-words max-md:text-left">
            <Layers className="h-4 w-4 text-muted-foreground max-md:shrink-0" />
            {readOnly ? 'View Import' : 'Import to case'} — {document.filename}
          </DialogTitle>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7 max-md:size-9 max-md:shrink-0">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </DialogClose>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
          </div>
        ) : usePdfLayout && isStructuredDoc ? (
          /* ── Structured PDF: page viewer + sidebar ── */
          <div className={SPLIT_BODY}>
            {/* Left: PDF page selector */}
            <div className={SPLIT_VIEWER}>
              {/* Info banner */}
              <div className="flex-shrink-0 m-3 rounded-lg bg-muted border border-border p-3 flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  This document uses page-by-page vision extraction. Ensure your selection includes
                  complete headers, full transaction tables, and all surrounding context needed for
                  accurate extraction.
                </p>
              </div>
              <PdfPageSelector
                pdfSource={pdfSource}
                pageCount={pageCount ?? 0}
                selectedPages={selectedPageNumbers}
                onTogglePage={page =>
                  !readOnly &&
                  setSelectedPageNumbers(prev => {
                    const next = new Set(prev)
                    if (next.has(page)) {
                      next.delete(page)
                    } else {
                      next.add(page)
                    }
                    return next
                  })
                }
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                currentPage={currentPage}
                onCurrentPageChange={setCurrentPage}
              />
            </div>

            {/* Right: Sidebar (reused with page-count variant) */}
            <div className={SPLIT_SIDEBAR}>
              <ModalSidebar
                selectedChunkIds={selectedChunkIds}
                selectedPages={selectedPages}
                selectedPageNumbers={selectedPageNumbers}
                isStructuredDoc={isStructuredDoc}
                onClearAll={() => {
                  setSelectedChunkIds(new Set())
                  setSelectedPages(new Set())
                  setSelectedPageNumbers(new Set())
                }}
                cases={cases}
                selectedCaseId={selectedCaseId}
                onCaseChange={setSelectedCaseId}
                scopeNote={scopeNote}
                onNoteChange={setScopeNote}
                onSubmit={handleSubmit}
                submitting={submitting}
                error={error}
                successMsg={successMsg}
                existingScopes={existingScopes}
                onDeleteScope={handleDeleteScope}
                readOnly={readOnly}
              />
            </div>
          </div>
        ) : useImageLayout ? (
          /* ── Image document: image viewer + sidebar ── */
          <div className={SPLIT_BODY}>
            {/* Left: Image viewer */}
            <div className={SPLIT_VIEWER}>
              <ImagePageSelector
                document={document}
                isSelected={selectedPageNumbers.has(1)}
                onToggle={() => {
                  if (readOnly) return
                  setSelectedPageNumbers(prev => {
                    const next = new Set(prev)
                    if (next.has(1)) {
                      next.delete(1)
                    } else {
                      next.add(1)
                    }
                    return next
                  })
                }}
              />
            </div>

            {/* Right: Sidebar */}
            <div className={SPLIT_SIDEBAR}>
              <ModalSidebar
                selectedChunkIds={selectedChunkIds}
                selectedPages={selectedPages}
                selectedPageNumbers={selectedPageNumbers}
                isStructuredDoc={true}
                onClearAll={() => {
                  setSelectedChunkIds(new Set())
                  setSelectedPages(new Set())
                  setSelectedPageNumbers(new Set())
                }}
                cases={cases}
                selectedCaseId={selectedCaseId}
                onCaseChange={setSelectedCaseId}
                scopeNote={scopeNote}
                onNoteChange={setScopeNote}
                onSubmit={handleSubmit}
                submitting={submitting}
                error={error}
                successMsg={successMsg}
                existingScopes={existingScopes}
                onDeleteScope={handleDeleteScope}
                readOnly={readOnly}
              />
            </div>
          </div>
        ) : usePdfLayout ? (
          /* ── PDF / DOCX two-column layout ── */
          <div className={SPLIT_BODY}>
            {/* Left: page selector + chunk list */}
            <div className={SPLIT_VIEWER}>
              {isDocx ? (
                <DocxPageSelector
                  document={document}
                  pageCount={pageCount ?? 0}
                  selectedPages={selectedPages}
                  onTogglePage={handleTogglePage}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  currentPage={currentPage}
                  onCurrentPageChange={setCurrentPage}
                >
                  {viewMode === 'single' && !selectWholeDocument && (
                    <div className="pb-6">
                      {loadingChunksForPage === currentPage ? (
                        <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading chunks...
                        </div>
                      ) : currentPageChunks !== null ? (
                        <PageChunkList
                          chunks={currentPageChunks}
                          selectedChunkIds={selectedChunkIds}
                          onToggleChunk={readOnly ? () => {} : handleToggleChunk}
                          highlightChunkId={
                            currentPage === (initialChunkPageNumber ?? 1)
                              ? initialChunkId
                              : undefined
                          }
                        />
                      ) : null}
                    </div>
                  )}
                </DocxPageSelector>
              ) : (
                <PdfPageSelector
                  pdfSource={pdfSource}
                  pageCount={pageCount ?? 0}
                  selectedPages={selectedPages}
                  onTogglePage={handleTogglePage}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  currentPage={currentPage}
                  onCurrentPageChange={setCurrentPage}
                >
                  {/* Chunk list rendered below PDF page in the same scroll area */}
                  {viewMode === 'single' && !selectWholeDocument && (
                    <div className="pb-6">
                      {loadingChunksForPage === currentPage ? (
                        <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading chunks...
                        </div>
                      ) : currentPageChunks !== null ? (
                        <PageChunkList
                          chunks={currentPageChunks}
                          selectedChunkIds={selectedChunkIds}
                          onToggleChunk={readOnly ? () => {} : handleToggleChunk}
                          highlightChunkId={
                            currentPage === (initialChunkPageNumber ?? 1)
                              ? initialChunkId
                              : undefined
                          }
                        />
                      ) : null}
                    </div>
                  )}
                </PdfPageSelector>
              )}
            </div>

            {/* Right: Sidebar */}
            <div className={SPLIT_SIDEBAR}>
              <ModalSidebar
                selectedChunkIds={selectedChunkIds}
                selectedPages={selectedPages}
                selectedPageNumbers={selectedPageNumbers}
                isStructuredDoc={isStructuredDoc || selectWholeDocument}
                onClearAll={() => {
                  setSelectedChunkIds(new Set())
                  setSelectedPages(new Set())
                }}
                cases={cases}
                selectedCaseId={selectedCaseId}
                onCaseChange={setSelectedCaseId}
                scopeNote={scopeNote}
                onNoteChange={setScopeNote}
                onSubmit={handleSubmit}
                submitting={submitting}
                error={error}
                successMsg={successMsg}
                existingScopes={existingScopes}
                onDeleteScope={handleDeleteScope}
                readOnly={readOnly}
              />
            </div>
          </div>
        ) : (
          /* ── Non-PDF / whole-document simple form ── */
          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            <SimpleForm
              document={document}
              isAudio={isAudio}
              selectWholeDocument={selectWholeDocument}
              cases={cases}
              selectedCaseId={selectedCaseId}
              onCaseChange={setSelectedCaseId}
              scopeNote={scopeNote}
              onNoteChange={setScopeNote}
              onSubmit={handleSubmit}
              submitting={submitting}
              error={error}
              successMsg={successMsg}
              existingScopes={existingScopes}
              onDeleteScope={handleDeleteScope}
              readOnly={readOnly}
            />
          </div>
        )}
      </DialogContent>

      <AlertDialog
        open={replaceConfirmOpen}
        onOpenChange={open => {
          if (!open) setReplaceConfirmOpen(false)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace existing import?</AlertDialogTitle>
            <AlertDialogDescription>
              This document is already imported into{' '}
              <span className="font-medium text-foreground">
                {cases.find(c => c.id === selectedCaseId)?.name ?? 'this case'}
              </span>
              . Re-importing will update the import scope in place — no duplicate will be created.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setReplaceConfirmOpen(false)
                void executeSubmit()
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Sidebar (PDF layout)
// ---------------------------------------------------------------------------

interface ModalSidebarProps {
  selectedChunkIds: Set<string>
  selectedPages: Set<number>
  selectedPageNumbers: Set<number>
  isStructuredDoc: boolean
  onClearAll: () => void
  cases: Case[]
  selectedCaseId: string
  onCaseChange: (id: string) => void
  scopeNote: string
  onNoteChange: (note: string) => void
  onSubmit: () => void
  submitting: boolean
  error: string | null
  successMsg: string | null
  existingScopes: ExtractionScope[]
  onDeleteScope: (id: string) => void
  readOnly?: boolean
}

function ModalSidebar({
  selectedChunkIds,
  selectedPages,
  selectedPageNumbers,
  isStructuredDoc,
  onClearAll,
  cases,
  selectedCaseId,
  onCaseChange,
  scopeNote,
  onNoteChange,
  onSubmit,
  submitting,
  error,
  successMsg,
  existingScopes,
  onDeleteScope,
  readOnly = false,
}: ModalSidebarProps) {
  // The sidebar tracks one count: the number of pages that will be sent to
  // the case. For structured docs the source is selectedPageNumbers (vision
  // page selector); for narrative PDFs/DOCX the source is selectedPages
  // (the page-card toggle). Chunk-level selection is implicitly converted to
  // its covering pages on submit, but doesn't drive the sidebar count.
  const pageCount = isStructuredDoc ? selectedPageNumbers.size : selectedPages.size
  const chunkCount = selectedChunkIds.size
  const hasSelection = pageCount > 0 || (!isStructuredDoc && chunkCount > 0)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            Case Import
          </h3>
          {hasSelection && !readOnly && (
            <button
              onClick={onClearAll}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {/* Selection summary */}
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            Selected pages
          </p>
          {hasSelection ? (
            <div className="flex items-center gap-2 text-xs text-success">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {pageCount > 0
                ? `${pageCount} page${pageCount !== 1 ? 's' : ''} selected`
                : `${chunkCount} chunk${chunkCount !== 1 ? 's' : ''} selected`}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              {readOnly
                ? 'Entire document'
                : 'Select pages below, or entire document will be imported'}
            </p>
          )}
        </div>

        <Separator />

        {/* Feedback (only in edit mode) */}
        {!readOnly && error && (
          <div className="rounded bg-destructive-surface border border-destructive-border p-2 flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 text-destructive-foreground flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-destructive-foreground">{error}</p>
          </div>
        )}
        {!readOnly && successMsg && (
          <div className="rounded bg-success-surface border border-success-border p-2 flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-success-foreground flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-success-foreground">{successMsg}</p>
          </div>
        )}

        {/* Form */}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">
              Target Case
            </Label>
            {readOnly ? (
              <p className="text-xs text-foreground px-1">
                {cases.find(c => c.id === selectedCaseId)?.name || selectedCaseId || '—'}
              </p>
            ) : (
              <Select value={selectedCaseId} onValueChange={onCaseChange}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select a case" />
                </SelectTrigger>
                <SelectContent className="z-[300]">
                  {cases.map(c => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {(scopeNote || !readOnly) && (
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">
                Note
              </Label>
              {readOnly ? (
                <p className="text-xs text-muted-foreground px-1 whitespace-pre-wrap">
                  {scopeNote}
                </p>
              ) : (
                <Textarea
                  value={scopeNote}
                  onChange={e => onNoteChange(e.target.value)}
                  placeholder="Optional note describing this import…"
                  className="text-xs min-h-[52px] resize-none"
                  maxLength={500}
                />
              )}
            </div>
          )}
        </div>

        {/* Scope type preview */}
        <div className="rounded-lg border border-border bg-muted/20 p-3">
          <div className="flex items-start gap-2">
            <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground">
              {hasSelection
                ? pageCount > 0
                  ? `${pageCount} page${pageCount !== 1 ? 's' : ''} will be imported`
                  : `${chunkCount} chunk${chunkCount !== 1 ? 's' : ''} will be imported`
                : 'Entire document will be imported'}
            </p>
          </div>
        </div>

        {/* Re-import notice — shown when the chosen case already has an
            import for this lake document. Submitting will update the
            existing import in place rather than create a new one. */}
        {!readOnly && selectedCaseId && existingScopes.some(s => s.case_id === selectedCaseId) && (
          <div className="rounded-lg border border-warning-border bg-warning-surface p-3">
            <p className="text-[11px] text-warning-foreground leading-relaxed">
              {(() => {
                const existing = existingScopes.find(s => s.case_id === selectedCaseId)
                if (!existing) return null
                const dt = new Date(existing.created_at)
                const when = Number.isNaN(dt.getTime())
                  ? ''
                  : ` — last set ${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`
                return <>Updating existing import{when}. Submitting replaces its scope.</>
              })()}
            </p>
          </div>
        )}

        {/* Existing scopes (only in edit mode) */}
        {!readOnly && (
          <ExistingScopesList
            scopes={existingScopes}
            cases={cases}
            onDelete={onDeleteScope}
            compact
          />
        )}
      </div>

      {/* Sticky submit (hidden in readOnly) */}
      {!readOnly && (
        <div className="p-4 border-t border-border bg-card flex-shrink-0">
          <Button
            onClick={onSubmit}
            disabled={submitting || !selectedCaseId}
            className="w-full"
            size="sm"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                Importing…
              </>
            ) : hasSelection ? (
              pageCount > 0 ? (
                `Import ${pageCount} page${pageCount !== 1 ? 's' : ''} to case`
              ) : (
                `Import ${chunkCount} chunk${chunkCount !== 1 ? 's' : ''} to case`
              )
            ) : (
              'Import whole document to case'
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Simple form (non-PDF or whole-document mode)
// ---------------------------------------------------------------------------

interface SimpleFormProps {
  document: LakeDocument
  isAudio: boolean
  selectWholeDocument: boolean
  cases: Case[]
  selectedCaseId: string
  onCaseChange: (id: string) => void
  scopeNote: string
  onNoteChange: (note: string) => void
  onSubmit: () => void
  submitting: boolean
  error: string | null
  successMsg: string | null
  existingScopes: ExtractionScope[]
  onDeleteScope: (id: string) => void
  readOnly?: boolean
}

function SimpleForm({
  document,
  isAudio,
  selectWholeDocument,
  cases,
  selectedCaseId,
  onCaseChange,
  scopeNote,
  onNoteChange,
  onSubmit,
  submitting,
  error,
  successMsg,
  existingScopes,
  onDeleteScope,
  readOnly = false,
}: SimpleFormProps) {
  const wholeLabel = isAudio ? 'Whole file' : 'Whole document'
  const durationSecs = document.audio_duration_seconds ?? null
  const durationLabel =
    durationSecs != null
      ? `${Math.floor(durationSecs / 60)}m ${Math.round(durationSecs % 60)}s`
      : null
  const existingForCase = existingScopes.find(s => s.case_id === selectedCaseId)
  return (
    <div className="space-y-6">
      {!readOnly && error && (
        <div className="rounded-lg bg-destructive-surface border border-destructive-border p-3 flex items-start gap-2 text-xs text-destructive-foreground">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}
      {!readOnly && successMsg && (
        <div className="rounded-lg bg-success-surface border border-success-border p-3 flex items-start gap-2 text-xs text-success-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          {successMsg}
        </div>
      )}

      <div className="rounded-lg bg-muted/30 border border-border p-4 space-y-4">
        <h3 className="text-sm font-medium text-foreground">
          {readOnly ? 'Import' : `Import to case — ${wholeLabel}`}
        </h3>

        {isAudio && (
          <div className="rounded-md border border-border bg-muted/40 p-3 flex items-start gap-2">
            <FileAudio className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Audio files are imported as a whole — page selection is not available.
              {durationLabel && (
                <>
                  {' '}
                  <span className="text-foreground">Duration:</span> {durationLabel}.
                </>
              )}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Target Case</Label>
          {readOnly ? (
            <p className="text-sm text-foreground">
              {cases.find(c => c.id === selectedCaseId)?.name || selectedCaseId || '—'}
            </p>
          ) : (
            <Select value={selectedCaseId} onValueChange={onCaseChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a case" />
              </SelectTrigger>
              <SelectContent className="z-[300]">
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {(scopeNote || !readOnly) && (
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Note (optional)</Label>
            {readOnly ? (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {scopeNote || '—'}
              </p>
            ) : (
              <Textarea
                value={scopeNote}
                onChange={e => onNoteChange(e.target.value)}
                placeholder="Add a note to describe this scope..."
                className="text-xs min-h-[60px]"
                maxLength={500}
              />
            )}
          </div>
        )}

        {!readOnly && existingForCase && (
          <div className="rounded-md border border-warning-border bg-warning-surface p-3">
            <p className="text-[11px] text-warning-foreground leading-relaxed">
              {(() => {
                const dt = new Date(existingForCase.created_at)
                const when = Number.isNaN(dt.getTime())
                  ? ''
                  : ` — last set ${dt.toLocaleDateString()} ${dt.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`
                return <>Updating existing import{when}. Submitting replaces its scope.</>
              })()}
            </p>
          </div>
        )}

        {!readOnly && (
          <Button onClick={onSubmit} disabled={submitting || !selectedCaseId} className="w-full">
            {submitting ? (
              <>
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                Importing…
              </>
            ) : isAudio ? (
              'Import whole file to case'
            ) : (
              'Import whole document to case'
            )}
          </Button>
        )}
      </div>

      {!readOnly && (
        <ExistingScopesList scopes={existingScopes} cases={cases} onDelete={onDeleteScope} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Existing scopes list
// ---------------------------------------------------------------------------

interface ExistingScopesListProps {
  scopes: ExtractionScope[]
  cases: Case[]
  onDelete: (id: string) => void
  compact?: boolean
}

function ExistingScopesList({ scopes, cases, onDelete, compact = false }: ExistingScopesListProps) {
  if (scopes.length === 0) return null

  return (
    <div className="space-y-2">
      <h3
        className={`font-medium text-muted-foreground ${compact ? 'text-[11px] uppercase tracking-wide' : 'text-sm'}`}
      >
        Existing imports
      </h3>
      {scopes.map(scope => {
        const scopeCase = cases.find(c => c.id === scope.case_id)
        return (
          <div
            key={scope.id}
            className="rounded-lg bg-muted/30 border border-border p-3 flex items-start justify-between gap-3"
          >
            <div className="flex-1 min-w-0">
              <div
                className={`text-foreground font-medium mb-0.5 ${compact ? 'text-xs' : 'text-sm'}`}
              >
                → {scopeCase?.name || scope.case_id}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {scope.scope_type === 'all' ? (
                  <span>Entire document</span>
                ) : scope.scope_type === 'pages' ? (
                  <span>{scope.selected_pages?.length ?? 0} pages</span>
                ) : (
                  <span>{scope.selected_chunk_ids?.length ?? 0} chunks</span>
                )}
                {scope.scope_note && (
                  <span className="ml-1.5 text-muted-foreground/60">· {scope.scope_note}</span>
                )}
              </div>
              {!compact && (
                <div className="text-[10px] text-muted-foreground/50 mt-0.5">
                  {new Date(scope.created_at).toLocaleString()}
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
              onClick={() => onDelete(scope.id)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )
      })}
    </div>
  )
}
